import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { SocketContext } from '../Socket/SocketContext';
import useWhatsApps from '../../hooks/useWhatsApps';
import toastError from '../../errors/toastError';

const PendingMessagesContext = createContext();

const PENDING_STORAGE_KEY = "crmPendingMessages";
const PENDING_TTL_MS = 60 * 60 * 1000; // 1 hora

const loadPersistedPendingMessages = () => {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const now = Date.now();
    return parsed.map((msg) => {
      const createdAtMs = Date.parse(msg?.createdAt);
      const isStale = Number.isFinite(createdAtMs) && now - createdAtMs >= PENDING_TTL_MS;
      if (isStale && msg.status !== "failed") {
        return { ...msg, status: "failed" };
      }
      // Uma mensagem restaurada como "sending" não está mais em voo de fato
      // (a página recarregou) — volta para "pending" para ser retentada.
      if (msg.status === "sending") {
        return { ...msg, status: "pending" };
      }
      return msg;
    });
  } catch (error) {
    return [];
  }
};

export const PendingMessagesProvider = ({ children }) => {
  const [pendingMessages, setPendingMessages] = useState(loadPersistedPendingMessages);
  const socketManager = useContext(SocketContext);

  // Persiste a fila de pending para sobreviver a um reload da página
  useEffect(() => {
    try {
      localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pendingMessages));
    } catch (error) {
      // Quota excedida ou erro de serialização: segue funcionando só em memória
    }
  }, [pendingMessages]);
  const { whatsApps } = useWhatsApps();

  const companyId = localStorage.getItem('companyId');

  // Add a pending message to queue
  const queueMessage = useCallback((msg) => {
    // msg must include at least ticketId, body, userName, quotedMsg, createdAt, tempId, whatsappId
    setPendingMessages(prev => [...prev, msg]);
  }, []);

  const removePendingMessage = useCallback((tempId) => {
    setPendingMessages(prev => prev.filter(m => m.tempId !== tempId));
  }, []);

  const removePendingMessageByOptimisticId = useCallback((optimisticId) => {
    setPendingMessages(prev => prev.filter(m => m.optimisticId !== optimisticId));
  }, []);

  // Try to send queued messages for a given whatsappId
  const trySendQueuedForWhatsapp = useCallback(async (whatsappId) => {
    const toSend = pendingMessages.filter(m => m.whatsappId === whatsappId && m.status !== 'sending');
    if (toSend.length === 0) return;

    for (const m of toSend) {
      // mark as sending
      setPendingMessages(prev => prev.map(pm => pm.tempId === m.tempId ? { ...pm, status: 'sending' } : pm));
      try {
        await api.post(`/messages/${m.ticketId}`, {
          read: 1,
          fromMe: true,
          mediaUrl: '',
          userName: m.userName,
          body: m.body,
          mentionedJids: m.mentionedJids || [],
          quotedMsg: m.quotedMsg,
        });
        // remove from pending queue - backend will emit created message
        removePendingMessage(m.tempId);
      } catch (err) {
        // If still transient (wapp not CONNECTED/initialized), leave it pending
        const code = err?.response?.data?.error;
        if (code === 'ERR_WAPP_NOT_INITIALIZED' || code === 'ERR_WAPP_NOT_CONNECTED') {
          // revert status to pending and keep for retry
          setPendingMessages(prev => prev.map(pm => pm.tempId === m.tempId ? { ...pm, status: 'pending' } : pm));
        } else {
          // permanent error: mark failed and notify
          setPendingMessages(prev => prev.map(pm => pm.tempId === m.tempId ? { ...pm, status: 'failed', error: err } : pm));
          toastError(err);
        }
      }
    }
  }, [pendingMessages, removePendingMessage]);

  // Listen to whatsapp status changes via whatsApps (updated by hook)
  useEffect(() => {
    // When any whatsapp updates, try to send messages for that whatsapp if it became CONNECTED
    whatsApps.forEach(w => {
      if (w.status === 'CONNECTED') {
        trySendQueuedForWhatsapp(w.id);
      }
    });
  }, [whatsApps, trySendQueuedForWhatsapp]);

  // Also listen to socket events for immediate retries
  useEffect(() => {
    if (!companyId) return;
    const socket = socketManager.getSocket(companyId);

    const handler = (data) => {
      if (data?.action === 'update' && data?.whatsapp && data.whatsapp.status === 'CONNECTED') {
        trySendQueuedForWhatsapp(data.whatsapp.id);
      }
    };

    socket.on(`company-${companyId}-whatsapp`, handler);
    return () => {
      try { socket.off(`company-${companyId}-whatsapp`, handler); } catch(e) {}
    };
  }, [companyId, socketManager, trySendQueuedForWhatsapp]);

  const getPendingForTicket = useCallback((ticketId) => {
    return pendingMessages.filter(m => m.ticketId === ticketId);
  }, [pendingMessages]);

  return (
    <PendingMessagesContext.Provider value={{ pendingMessages, queueMessage, getPendingForTicket, removePendingMessage, removePendingMessageByOptimisticId }}>
      {children}
    </PendingMessagesContext.Provider>
  );
};

export default PendingMessagesContext;
